import {
  OutdoorDeliverType,
  SpaceDeliverabilityData,
  SpaceDevice,
  ViewTrackingTrigger,
  ViewTrackingType
} from 'core/rtbCampaign/RtbCampaign';
import {
  DefaultRtbCampaignBasicFormModel,
  LimitationConfig,
  RtbCampaignBasicFormModel,
  RtbCampaignBasicFormModelConstructorParams,
  RtbCampaignBasicFormState
} from './RtbCampaignBasicFormModel';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import {
  AdRequestSourceManager,
  DefaultAdRequestSourceManager,
  deviceCharacteristicInfoExtraGetter,
  storeInfoExtraGetter
} from 'core/adRequestSource/AdRequestSourceManager';
import {
  DefaultProductGroupManager,
  ProductGroupManager
} from 'core/limitation/ProductGroupManager';
import { renderSpacesOfAttribute } from './components/SpacesOfAttribute';
import { OPERATE, OPERATES } from 'enum/Operate';
import { aggregateHealthState, AggregatedSpaceHealth } from 'utils/spaceHealth';
import { renderSpaceHealthTooltip } from 'components/CampaignLimitation/SpaceHealthTooltip/SpaceHealthTooltip';
import {
  DEFAULT_INVENTORY,
  LIMITATION_TYPE,
  LimitationInventorySettings
} from 'containers/Limitations/LimitationSetting/limitationConfig/limitationSettingsType';
import {
  Device,
  DeviceMap,
  SpaceAttribute,
  SpaceAttributeMap,
  SpaceAttributes,
  SpaceDetailMap
} from 'components/CampaignLimitation/CustomOutdoorDevice/DeviceDataContext';
import i18n from 'i18n';
import _, { Dictionary } from 'lodash';
import config from 'config';
import {
  buildValueLabelMap,
  createSelectOptionsFromEnum
} from 'utils/SelectOptionsUtils';
import { defaultItemSetting } from 'components/CampaignLimitation/SelectItemComponent';
import { SelectComponent } from 'components/CampaignLimitation/SelectComponent';
import { renderProductGroupTips } from 'containers/Limitations/LimitationSetting/ProductGroupTips';
import {
  DefaultRtbCampaignManager,
  RtbCampaignManager
} from 'core/rtbCampaign/RtbCampaignManager';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { DefaultAsyncSourceSpanModel } from 'components/AsyncSourceSpan/AsyncSourceSpanModel';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { renderSpacesOfSynchronizedPlaybackGroup } from './components/SpacesOfSynchronizedPlaybackGroup';
import {
  ChannelGroupManager,
  DefaultChannelGroupManager
} from 'core/channelGroup/ChannelGroupManager';
import { DeviceUnionFind } from 'core/googleMaps/DeviceUnionFind';
import { EditLimitationModel } from 'containers/Limitations/EditLimitationModel';
import { DeliveryUnit } from 'core/channelGroup/ChannelGroup';
import {
  AecoSignManager,
  DefaultAecoSignManager
} from 'core/aecosign/AecoSignManager';
import { isEpaperChannelGroup } from 'core/channelGroup/EpaperChannelGroup';
import { appendSelectedNotInOptions } from './selectedDeliveryTargetOptions';

export interface OutdoorLimitaitonCampaignFormModel extends RtbCampaignBasicFormModel {
  readonly canEditBudgetPlan: boolean;
  readonly canEditPriceModel: boolean;
  setLimitationSettingResetHook: (
    hook: (
      limitationSetting: LimitationInventorySettings[],
      rerender?: boolean
    ) => void
  ) => void;
  /**
   * 由表單層同步 fixOutdoorSpace 的「即時值」（Formik 開關可即時切換，
   * campaignBasic 只反映載入時的值）。getChipHealth 需要即時值才能在使用者
   * 一開啟固定門市時就改用單台 spaceId 狀態。
   */
  setLiveFixOutdoorSpace: (fixOutdoorSpace: boolean) => void;
  /**
   * 由表單層同步走期的「即時值」（ECOPD-5615）。
   *
   * 電子紙的機台名額是**逐日**判定的，走期一改，能選的機台就跟著變 —— 用
   * campaignBasic 上的值只反映載入時的走期，使用者改完日期後看到的灰階會是舊的。
   */
  setLiveDateRange: (startDate?: string, endDate?: string) => void;
  /** Per-attribute value→label maps from the freshest options fetch. */
  readonly spaceAttributeLabelMaps: Map<string, Map<string | number, string>>;
  /**
   * 由設定流程在建立限制編輯器之前交出活動已存的限制值，供選項面板補回已從選項樹
   * 消失的門市／機台（POS-8118）。
   */
  rememberSelectedDeliveryTargets: (limitations: LimitationData) => void;
}

export type OutdoorLimitaitonCampaignFormState = RtbCampaignBasicFormState & {
  readonly spaceAttributeMap: SpaceAttributeMap;
  readonly deviceMap: DeviceMap;
  readonly spaceDetailMap: SpaceDetailMap;
};

const rtbCmapignManager: RtbCampaignManager = new DefaultRtbCampaignManager();
const adRequestSourceManager: AdRequestSourceManager =
  new DefaultAdRequestSourceManager();
const productGroupManager: ProductGroupManager =
  new DefaultProductGroupManager();
const aecoSignManager: AecoSignManager = new DefaultAecoSignManager();
/**
 * 會被「機台名額已滿」灰掉的 limitation 型別（ECOPD-5615）。
 *
 * 這兩個型別的選項就是**一台機器或一間門市**；群組型（cityGroup / administrativeArea /
 * outdoorPlaceCategories / simulcastGroup）刻意不列 —— Final Spec 要求群組仍可選。
 */
const SINGLE_SPACE_LIMITATION_TYPES = ['storeNum', 'deviceCharacteristic'];
const attributeTypeFieldMap: {
  [key: string]: (spaceDevice: SpaceDevice) => {
    key: string | string[];
    subKey?: string | string[];
    sortKey?: (attr: SpaceAttribute) => string;
  };
} = {
  administrativeArea: (d) => ({
    key: d.municipality,
    subKey: `${d.municipality}/${d.district}`
  }),
  cityGroup: (d) => ({
    key: d.region
  }),
  outdoorPlaceCategories: (d) => ({
    key: _.keys(d.outdoorPlaceCategories),
    subKey: _.flatMap(
      _.entries(d.outdoorPlaceCategories),
      ([groupKey, items]) => items.map((item) => `${groupKey}/${item}`)
    ),
    sortKey: (attr) => attr.value.toString()
  }),
  storeNum: (d) => ({
    key: d.municipality,
    subKey: storeInfoExtraGetter(d)
  }),
  deviceCharacteristic: (d) => ({
    key: d.municipality,
    subKey: deviceCharacteristicInfoExtraGetter(d)
  })
};
export abstract class AbstractOutdoorLimitationCampaignFormModel
  extends DefaultRtbCampaignBasicFormModel
  implements OutdoorLimitaitonCampaignFormModel
{
  adFormat = 'Outdoor';
  spaceAttributeSettings: LimitationInventorySettings[] = [];
  spaceAttributes: SpaceAttributes = {};
  sidebarTitleKey = 'limitation.titles.outdoorDeviceProperty';
  spaceAttributeMap: SpaceAttributeMap = {};
  deviceMap: DeviceMap = {};
  spaceDetailMap: SpaceDetailMap = {};
  // Per-attribute value→label maps from the freshest options fetch. Used by
  // overlayLimitationLabels to refresh stored limitation labels on load — the
  // saved label may be from a different locale than the current session.
  spaceAttributeLabelMaps: Map<string, Map<string | number, string>> =
    new Map();
  spaces: SelectOptions[] = [];
  spaceDevices: SpaceDevice[] = [];
  testMode = false;
  protected testModeCache: Map<
    boolean,
    {
      spaces: SelectOptions[];
      spaceDevices: SpaceDevice[];
      spaceAttributeMap: SpaceAttributeMap;
      deviceMap: DeviceMap;
      spaceDetailMap: SpaceDetailMap;
      spaceAttributeSettings: LimitationInventorySettings[];
      // POS-8002：storeNum label map 會在 getSpaceDevices 依當次 spaceDevices 補入
      // Banned 門市店名，屬於「隨 testMode 而異」的資料，必須一併快照/還原，避免切換
      // testMode 命中 cache 時殘留到另一個模式的門市名稱。
      spaceAttributeLabelMaps: Map<string, Map<string | number, string>>;
      // POS-8073：子類別自有的「隨 testMode 而異」狀態（例如同播的群組清單），
      // 由 captureTestModeExtras / restoreTestModeExtras 這對 hook 負責內容。
      extras?: unknown;
    }
  > = new Map();
  limitationSettingResetHook?: (
    limitationSetting: LimitationInventorySettings[],
    rerender?: boolean
  ) => void;
  // POS-8118：活動已存的門市／機台選取值。版位全部被 BANNED 之後就不在 store-options
  // （測試模式看的是測試池，同樣不含），選項面板會整列不見 —— chip 上看得到，面板裡
  // 卻確認不了也取消不掉。只在載入時給一次即可：本次操作新挑的必然在選項樹裡。
  private selectedSingleSpaceOptions: Map<string, SelectOptions[]> = new Map();

  rememberSelectedDeliveryTargets = (limitations: LimitationData) => {
    const byType = new Map<string, SelectOptions[]>();
    Object.values(limitations ?? {}).forEach((limitationsOfOperate) => {
      if (!Array.isArray(limitationsOfOperate)) {
        return;
      }
      limitationsOfOperate.forEach((limitation: Limitation) => {
        if (
          !SINGLE_SPACE_LIMITATION_TYPES.includes(limitation.type) ||
          !Array.isArray(limitation.value)
        ) {
          return;
        }
        byType.set(limitation.type, [
          ...(byType.get(limitation.type) ?? []),
          ...(limitation.value as SelectOptions[])
        ]);
      });
    });
    this.selectedSingleSpaceOptions = byType;
  };

  private withSelectedNotInOptions(
    type: string,
    cb: (
      operate?: OPERATES
    ) => Promise<SelectOptions[]> | SelectOptions[] | undefined
  ) {
    return async (operate?: OPERATES): Promise<SelectOptions[]> =>
      appendSelectedNotInOptions(
        (await cb(operate)) ?? [],
        this.selectedSingleSpaceOptions.get(type) ?? [],
        this.spaceAttributeLabelMaps.get(type)
      );
  }

  // fixOutdoorSpace 的即時值（來自 Formik）；undefined 時 fallback 到 campaignBasic。
  private liveFixOutdoorSpace?: boolean;

  setLiveFixOutdoorSpace = (fixOutdoorSpace: boolean) => {
    this.liveFixOutdoorSpace = fixOutdoorSpace;
  };

  private get isFixOutdoorSpace(): boolean {
    return this.liveFixOutdoorSpace ?? !!this.campaignBasic.fixOutdoorSpace;
  }

  // ECOPD-5615：走期內已被 10 檔活動綁滿的版位 id（已 stripSpacePrefix 正規化）。
  private fullSpaceIds: Set<string> = new Set();
  // 走期的即時值（來自 Formik）；undefined 時 fallback 到 campaignBasic。
  private liveStartDate?: string;
  private liveEndDate?: string;
  private fullOptionValues: Set<string> = new Set();
  private fullOptionValuesSource?: SpaceDevice[];
  private fullOptionValuesSourceIds?: Set<string>;
  // 名額查詢的流水號：走期／測試模式連續改兩次時會有兩個請求在飛，只有最後發出的
  // 那個算數。少了它，先發後到的舊答案會蓋掉新答案 —— 而「剛變滿的機台短暫變回可選」
  // 這個方向會讓使用者選下去，等到物化才發現排不進去。
  private fullSpaceIdsSeq = 0;

  private get dateRange(): { startDate?: string; endDate?: string } {
    return {
      startDate: this.liveStartDate ?? this.campaignBasic.startDate,
      endDate: this.liveEndDate ?? this.campaignBasic.endDate
    };
  }

  setLiveDateRange = (startDate?: string, endDate?: string) => {
    if (this.liveStartDate === startDate && this.liveEndDate === endDate) {
      return;
    }
    this.liveStartDate = startDate;
    this.liveEndDate = endDate;
    // 這支是在 render 途中被呼叫的（與 setLiveFixOutdoorSpace 同一條路），
    // 所以不能 await —— 只在名額真的變了的時候才由 refresh 內部觸發重繪。
    this.refreshFullSpaceIds();
  };

  /**
   * 重抓「已被 10 檔活動綁滿」的機台清單（ECOPD-5615）。
   *
   * 只有電子紙渠道有這個限制，其餘渠道不打這支 API。名額真的變了才通知畫面重繪，
   * 否則會與 render 期間的 setLiveDateRange 互相觸發。
   */
  async refreshFullSpaceIds(): Promise<void> {
    if (!isEpaperChannelGroup(this.outdoorChannelId)) {
      return;
    }
    const { startDate, endDate } = this.dateRange;
    if (!startDate || !endDate) {
      return;
    }
    const seq = ++this.fullSpaceIdsSeq;
    try {
      const fullSpaceIds = await aecoSignManager.getFullSpaceIds(
        this.outdoorChannelId,
        startDate,
        endDate,
        this.testMode,
        // 🔴 編輯既有活動時一定要帶：後端會把這檔活動自己從佔用數扣掉，不帶的話
        // 使用者一進編輯就會看到自己現在鎖著的機台變灰、存不回去。
        this.campaignBasic.id ?? null
      );
      if (seq !== this.fullSpaceIdsSeq) {
        // 已經有更新的請求發出去了，這份答案問的是舊的走期／舊的機台池。
        return;
      }
      if (_.isEqual(fullSpaceIds, this.fullSpaceIds)) {
        return;
      }
      this.fullSpaceIds = fullSpaceIds;
      this.limitationSettingResetHook?.(
        this.getLimitationInventorySetting(),
        true
      );
      this.event.fireEvent(this);
    } catch (e) {
      // 保留上一次的清單而不是清空：清空等於「都可以選」，使用者會選下去、存進去，
      // 然後在物化階段才發現排不進去。
      console.error(e);
    }
  }

  /**
   * 把「已滿的版位 id」換算成「選項的值」。
   *
   * spaceDevices 的 spaceId 已經被 remap 成選項的值（門市模式是
   * `municipality/storeNum`、機台模式再加上 deviceCharacteristic），真實版位 id 留在
   * rawSpaceId —— 與 getChipHealth 用的是同一組索引來源。
   *
   * ⚠️ 一個門市底下可能有多台機器，**只有全部都滿了**才算這個選項不能選。滿一台就
   * 整個門市灰掉會讓還有名額的機器選不到，那比多選一個排不進去的更糟。
   */
  private rebuildFullOptionValues(): void {
    if (
      this.fullOptionValuesSource === this.spaceDevices &&
      this.fullOptionValuesSourceIds === this.fullSpaceIds
    ) {
      return;
    }
    const values = new Set<string>();
    if (this.fullSpaceIds.size > 0) {
      const deliverable = this.spaceDevices.filter((device) =>
        this.isDeliverableSpace(device)
      );
      for (const [optionValue, devices] of Object.entries(
        _.groupBy(deliverable, 'spaceId')
      )) {
        const allFull = devices.every((device) =>
          this.fullSpaceIds.has(
            this.stripSpacePrefix(String(device.rawSpaceId ?? device.spaceId))
          )
        );
        if (allFull) {
          values.add(optionValue);
        }
      }
    }
    this.fullOptionValues = values;
    this.fullOptionValuesSource = this.spaceDevices;
    this.fullOptionValuesSourceIds = this.fullSpaceIds;
  }

  // 只清掉自己標紅過的值：deviceCharacteristic 這類欄位的 isError 也可能是
  // validateLimitationValue（選項已不存在）標的，不該被這裡一併抹掉。
  private overBindingLimitMarked: WeakSet<SelectOptions> = new WeakSet();

  /**
   * 已經選到「名額已滿」的門市／機台時，標紅並擋住送出（ECOPD-5615 / Final Spec）。
   *
   * 走期或測試模式改掉之後，本來合法的選擇會變成超限 —— 灰階只擋得住「還沒選的」，
   * 已經在裡面的要靠這道驗證讓使用者知道得把它移除。
   */
  validateOverBindingLimit = (
    _operate: string,
    value: SelectOptions[]
  ): string | undefined => {
    const over = value.filter((option) =>
      this.isOptionOverBindingLimit(option.value)
    );
    const overSet = new Set(over);
    value.forEach((option) => {
      if (overSet.has(option)) {
        option.isError = true;
        this.overBindingLimitMarked.add(option);
      } else if (this.overBindingLimitMarked.has(option)) {
        option.isError = false;
        this.overBindingLimitMarked.delete(option);
      }
    });
    return over.length > 0
      ? i18n.t('editLimitation.errors.spaceOverBindingLimit', {
          count: over.length
        })
      : undefined;
  };

  /**
   * 給選項清單當 hover 提示用的「為什麼不能選」（ECOPD-5615）。
   *
   * 與 isOptionOverBindingLimit 分開：limitation setting 的 optionDisabledGetter
   * 要的是給人看的字，驗證那側要的是布林。
   */
  getOverBindingLimitReason = (value: string | number): string | undefined =>
    this.isOptionOverBindingLimit(value)
      ? i18n.t('limitation.hints.overBindingLimit')
      : undefined;

  isOptionOverBindingLimit = (value: string | number): boolean => {
    this.rebuildFullOptionValues();
    return this.fullOptionValues.has(String(value));
  };

  /** ECOPD-5615: 目前不能選的選項值（給地圖用；選項清單那邊走 optionDisabledGetter）。 */
  get overBindingLimitOptionValues(): Set<string> {
    this.rebuildFullOptionValues();
    return this.fullOptionValues;
  }

  constructor(
    protected channelGroupSetting: ChannelGroupSetting,
    actionType: string,
    ...args: RtbCampaignBasicFormModelConstructorParams
  ) {
    super(actionType, ...args);
  }

  override get state(): OutdoorLimitaitonCampaignFormState {
    return {
      dailyBudgetType: this.modelDailyBudgetType,
      spaceAttributeMap: this.spaceAttributeMap,
      deviceMap: this.deviceMap,
      spaceDetailMap: this.spaceDetailMap
    };
  }

  abstract get notSupportSpaceAttributes(): string[];

  protected abstract get spaceExtraGetter(): (
    record: any
  ) => string | undefined;

  protected abstract get spaceCountI18nKey(): string;

  /**
   * 可投遞的版位狀態：正式模式只算 ON，測試模式只算 TESTING。
   * 其餘狀態（BANNED＝AsiaMax 已下架、OFF/SUSPENDED）不列入門市/裝置的
   * 數量與 hover 清單。對應 POS-7996：先前把 BANNED 一併算進去，導致
   * 「門市數量」與清單膨脹（例：嘉義市顯示 33，實際上線只有 12 家）。
   */
  protected get deliverableSpaceStatus(): string {
    return this.testMode ? 'TESTING' : 'ON';
  }

  /**
   * 狀態可投遞之外，版位還不能已在 RMax 刪除：被封存或渠道停用的版位 PMax 拉不到，
   * spaceStatus 會停在最後一次同步的 ON，只有 active 看得出它已經不存在。
   */
  protected isDeliverableSpace(
    device: Pick<SpaceDevice, 'spaceStatus' | 'active'>
  ): boolean {
    return (
      (device.spaceStatus ?? '').toUpperCase() ===
        this.deliverableSpaceStatus && device.active !== false
    );
  }

  /**
   * 可投遞門市的去重 key 集合（extra = municipality/storeNum[/deviceCharacteristic]），
   * 由未過濾的 spaceDevices 依可投遞狀態算出。「門市數量」與 hover 清單共用同一份，
   * 確保兩者數字與內容一致（POS-7996）。
   */
  protected get deliverableSpaceExtras(): Set<string> {
    return new Set(
      this.spaceDevices
        .filter((device) => this.isDeliverableSpace(device))
        .map((device) => this.spaceExtraGetter(device))
        .filter((extra): extra is string => !!extra)
    );
  }

  override get outdoorDeliverTypeOptions(): SelectOptions[] | undefined {
    return createSelectOptionsFromEnum(
      OutdoorDeliverType,
      'campaign.labels.outdoorDeliverType.'
    );
  }

  get mapsApiKey(): string {
    return config.googleMapsApiKey;
  }

  abstract getSpaceGroups(): Promise<SelectOptions[]>;

  get outdoorChannelId() {
    return this.channelGroupSetting.config.id;
  }

  override async init() {
    try {
      const spaceAttributes =
        await adRequestSourceManager.getOutdoorSpaceAttributes(
          this.outdoorChannelId,
          this.testMode
        );
      // Build label maps BEFORE getSpaces / getSpaceDevices — those use the
      // storeNum map to label results.
      this.spaceAttributeLabelMaps = new Map();
      for (const [name, options] of Object.entries(spaceAttributes)) {
        this.spaceAttributeLabelMaps.set(name, buildValueLabelMap(options));
      }
      this.spaces = await this.getSpaces();
      this.spaceDevices = await this.getSpaceDevices();
      const spaceAttributeSettings: LimitationInventorySettings[] =
        productGroupManager.getSpaceAttributesInventorySettings(
          this.adFormat,
          this.outdoorChannelId,
          { [`${this.adFormat}-${this.outdoorChannelId}`]: spaceAttributes },
          this.notSupportSpaceAttributes
        );
      this.spaceAttributeSettings = [];
      for (const setting of spaceAttributeSettings) {
        if (setting.itemSetting) {
          setting.title &&
            (this.spaceAttributes[setting.name] = {
              title: setting.title,
              options: spaceAttributes[setting.name]
            });
          // The "primary" single-store/version chip (storeNum in store mode,
          // deviceCharacteristic in device mode) resolves health from local
          // spaceDevices. Grouping chips (administrativeArea/cityGroup/…) keep
          // the spaces-list tooltip via the spaces-with-attributes API.
          const isPrimarySpaceSetting = setting.name === this.mapDeviceType;
          this.spaceAttributeSettings.push({
            ...setting,
            addonFeature: `${LIMITATION_TYPE.CAMPAIGN}_channel_${this.outdoorChannelId}_${setting.name}`,
            i18nPrefix: `productGroup.space.attributes.${setting.name}`,
            supportOperates: [OPERATE.INCLUDE, OPERATE.EXCLUDE],
            sidebarTitleKey: this.sidebarTitleKey,
            healthGetter: (option) => this.getChipHealth(option),
            // ECOPD-5615：只有「單台」層級的選項要灰掉（門市／機台）。群組型選項
            // （地理分區、行政區、區域特性）依 Final Spec 仍可選，拆分時自然只會拿到
            // 還有名額的機台。
            ...(SINGLE_SPACE_LIMITATION_TYPES.includes(setting.name)
              ? {
                  optionDisabledGetter: this.getOverBindingLimitReason,
                  validator: this.validateOverBindingLimit,
                  ...(setting.cb
                    ? {
                        cb: this.withSelectedNotInOptions(
                          setting.name,
                          setting.cb
                        )
                      }
                    : {})
                }
              : {}),
            tipsConfig: {
              debounceTrigger: 500,
              renderTips: (attribute) => {
                const healthTip = renderSpaceHealthTooltip({
                  state: attribute.healthState,
                  spaceName: attribute.healthSpaceName,
                  reason: attribute.healthReason,
                  lastSyncedAt: attribute.healthLastSyncedAt,
                  deleted: attribute.healthDeleted
                });
                if (healthTip) return healthTip;
                // Primary chip with no local data → no tooltip, no API call.
                if (isPrimarySpaceSetting) return undefined;
                return renderSpacesOfAttribute(
                  this.spaceAttributeMap,
                  setting.name,
                  attribute.value.toString(),
                  'Outdoor',
                  this.outdoorChannelId,
                  false,
                  // POS-7996：hover 清單只列可投遞門市（排除 BANNED）且依門市去重，
                  // 與「門市數量」一致。fallback fetch 會帶 extraGetter/testMode，
                  // 再依 deliverableExtras 過濾去重。
                  {
                    extraGetter: this.spaceExtraGetter,
                    testMode: this.testMode,
                    deliverableExtras: this.deliverableSpaceExtras
                  }
                );
              }
            },
            titleHintPropsGetter: (limitation: Limitation) => {
              if (!limitation || !Array.isArray(limitation.value)) {
                return undefined;
              }
              const dataSource = () => {
                return adRequestSourceManager.getSpacesWithConditions(
                  false,
                  {
                    [setting.name]: (limitation.value as SelectOptions[]).map(
                      (option) => option.value.toString()
                    )
                  },
                  'Outdoor',
                  this.channelGroupSetting.config.id,
                  this.spaceExtraGetter,
                  this.testMode
                );
              };
              return this.spaceAttributes[setting.name]
                ? {
                    key: `title-hint-${setting.name}`,
                    model: new DefaultAsyncSourceSpanModel(
                      dataSource,
                      (spaces: SelectOptions[]) => {
                        // POS-7996：只計入可投遞（正式=ON、測試=TESTING）的門市，
                        // 排除 BANNED 等已下架版位，再依 extra（門市 key）去重。
                        const deliverableExtras = this.deliverableSpaceExtras;
                        return i18n.t(this.spaceCountI18nKey, {
                          count: Object.keys(
                            _.groupBy(
                              spaces.filter(
                                (space) =>
                                  !!space.extra &&
                                  deliverableExtras.has(space.extra.toString())
                              ),
                              'extra'
                            )
                          ).length
                        });
                      }
                    )
                  }
                : undefined;
            }
          });
        } else {
          this.spaceAttributeSettings.unshift(setting);
        }
      }
    } catch (e) {
      this.spaceAttributeSettings = [
        {
          name: DEFAULT_INVENTORY
        }
      ];
      console.error(e);
    }
    // 名額查詢與上面的選項查詢彼此獨立：查不到名額不該讓整個 limitation 掉回預設值，
    // 所以放在 try 外面自己吞錯（refreshFullSpaceIds 內部有 try）。
    await this.refreshFullSpaceIds();
  }

  get systemRecommendOutdoorDeviceInventorySetting(): LimitationInventorySettings {
    return {
      name: 'systemRecommendOutdoorDevice',
      title: 'limitation.labels.systemRecommendOutdoorDevice',
      ignoreValidateOption: true,
      supportOperates: [OPERATE.INCLUDE, OPERATE.EXCLUDE],
      singleSelect: false,
      itemSetting: {
        ...defaultItemSetting(),
        categorySelectable: false
      },
      searchPlaceholder: 'limitation.placeholders.searchRecommendOutdoorDevice',
      component: SelectComponent,
      addonFeature: `${LIMITATION_TYPE.CAMPAIGN}_channel_${this.outdoorChannelId}_systemRecommendOutdoorDevice`,
      sidebarTitleKey: this.sidebarTitleKey,
      needInitExtra: true,
      cb: async () => {
        const spacesGroups: SelectOptions[] = await this.getSpaceGroups();
        _.forEach(spacesGroups, (spaceGroup) => {
          const extra = _.defaultTo(spaceGroup.extra, []);
          _.forEach(extra, (space) => {
            const spaceId = space.value.toString();
            const spaceAttribute: SpaceAttribute = {
              label: spaceGroup.label,
              value: spaceGroup.value
            };
            const device: Device = {
              label: space.label,
              value: spaceId
            };
            const devices = _.get(
              this.spaceAttributeMap.systemRecommendOutdoorDevice,
              spaceAttribute.value.toString(),
              []
            );
            // Lodash _.set 的預設行為：如果 path 片段看起來像整數 index（例如 "9"、"15"），
            // 而父節點還不存在，就會建成 Array 而不是 Object。
            // 改用 setWith 強制建 Object
            _.setWith(
              this.spaceAttributeMap,
              `systemRecommendOutdoorDevice.${spaceAttribute.value.toString()}`,
              _.unionBy(devices, [device], 'value'),
              Object
            );
            const systemRecommendOutdoorDevicesOfDevice = _.get(
              this.deviceMap[spaceId],
              'systemRecommendOutdoorDevice',
              {
                title: i18n.t('limitation.labels.systemRecommendOutdoorDevice'),
                options: []
              }
            );
            _.set(this.deviceMap, `${spaceId}.systemRecommendOutdoorDevice`, {
              title: systemRecommendOutdoorDevicesOfDevice.title,
              options: _.unionBy(
                systemRecommendOutdoorDevicesOfDevice.options,
                [spaceAttribute],
                'value'
              )
            });
          });
        });
        return spacesGroups;
      },
      healthGetter: (option) => this.getChipHealth(option),
      tipsConfig: {
        renderTips: (space: SelectOptions) => {
          const healthTip = renderSpaceHealthTooltip({
            state: space.healthState,
            spaceName: space.healthSpaceName,
            reason: space.healthReason,
            lastSyncedAt: space.healthLastSyncedAt,
            deleted: space.healthDeleted
          });
          if (healthTip) return healthTip;
          return space.isGroup
            ? renderProductGroupTips(space, false)
            : undefined;
        }
      },
      titleHintPropsGetter: (limitation: Limitation) => {
        if (!limitation || !Array.isArray(limitation.value)) {
          return undefined;
        }
        const adTypeId = this.adTypeSetting?.adTypeId;
        if (!adTypeId) {
          return undefined;
        }
        const dataSource = () => {
          return this.rtbCampaignManager.getSpaceDeliverabilityData(
            this.channelGroupSetting.config.id,
            this.campaignBasic.adType,
            adTypeId,
            this.testMode,
            {
              include: [limitation],
              exclude: [],
              preferred: [],
              nonPreferred: [],
              other: []
            },
            // 編輯時要把自己從機台佔用數扣掉（ECOPD-5621）。
            this.campaignBasic.id ?? null
          );
        };
        return {
          key: 'title-hint-systemRecommendOutdoorDevice',
          model: new DefaultAsyncSourceSpanModel(
            dataSource,
            (deliverabilityData: SpaceDeliverabilityData) =>
              i18n.t('limitation.hints.storeCount', {
                count:
                  deliverabilityData.deliverable.length +
                  Object.values(
                    deliverabilityData.nonDeliverableByReason
                  ).reduce((sum, list) => sum + (list?.length ?? 0), 0)
              })
          )
        };
      }
    };
  }

  abstract getSpaces(): Promise<SelectOptions[]>;

  abstract getSpaceDevices(): Promise<SpaceDevice[]>;

  abstract setUpSpaceDetailMap(): Promise<void>;

  // Set the initial testMode before init() runs. Unlike setTestMode it skips
  // the cache swap / refetch, since no space data has been fetched yet — init()
  // will fetch with the correct testMode.
  setInitialTestMode = (testMode: boolean) => {
    this.testMode = testMode;
  };

  /**
   * POS-8073：子類別把「隨 testMode 而異」的自有狀態交出來一併快照。
   * 基底類別不解讀內容，只負責與 testModeCache 同進同出。
   */
  protected captureTestModeExtras(): unknown {
    return undefined;
  }

  /** POS-8073：與 captureTestModeExtras 對稱，cache hit 時把自有狀態還原回去。 */
  protected restoreTestModeExtras(_extras: unknown): void {
    // 基底類別沒有自有狀態，預設 no-op。
  }

  setTestMode = async (testMode: boolean) => {
    if (this.testMode === testMode) return;
    // 切換前先存目前的資料到 cache —— 只在這個 testMode 已經完成過 init+fetch
    // (即 cache 內已有 entry) 時才更新, 避免把 init 之前的空 snapshot 寫入,
    // 造成下次切回來時誤判 cache hit 而跳過 API 呼叫。
    if (this.testModeCache.has(this.testMode)) {
      this.testModeCache.set(this.testMode, {
        spaces: this.spaces,
        spaceDevices: this.spaceDevices,
        spaceAttributeMap: this.spaceAttributeMap,
        deviceMap: this.deviceMap,
        spaceDetailMap: this.spaceDetailMap,
        spaceAttributeSettings: this.spaceAttributeSettings,
        spaceAttributeLabelMaps: this.spaceAttributeLabelMaps,
        extras: this.captureTestModeExtras()
      });
    }
    this.testMode = testMode;
    const cached = this.testModeCache.get(testMode);
    if (cached) {
      this.spaces = cached.spaces;
      this.spaceDevices = cached.spaceDevices;
      this.spaceAttributeMap = cached.spaceAttributeMap;
      this.deviceMap = cached.deviceMap;
      this.spaceDetailMap = cached.spaceDetailMap;
      this.spaceAttributeSettings = cached.spaceAttributeSettings;
      this.spaceAttributeLabelMaps = cached.spaceAttributeLabelMaps;
      this.restoreTestModeExtras(cached.extras);
      this.limitationSettingResetHook &&
        this.limitationSettingResetHook(
          this.getLimitationInventorySetting(),
          true
        );
      this.event.fireEvent(this);
      // 測試機台與正式機台是兩池互不相通的庫存，名額要重新問一次（ECOPD-5615）。
      // cache miss 那條路會走到 init()，那裡已經抓過了。
      await this.refreshFullSpaceIds();
    } else {
      this.spaceAttributeMap = {};
      this.deviceMap = {};
      this.spaceDetailMap = {};
      await this.init();
      this.spaces = await this.getSpaces();
      this.spaceDevices = await this.getSpaceDevices();
      this.limitationSettingResetHook &&
        this.limitationSettingResetHook(
          this.getLimitationInventorySetting(),
          true
        );
      await this.fetchSpaceAndUpdateMaps();
    }
  };

  setLimitationSettingResetHook = (
    hook: (
      limitationSetting: LimitationInventorySettings[],
      rerender?: boolean
    ) => void
  ) => {
    this.limitationSettingResetHook = hook;
  };

  setUpAttributeMaps = (
    attributeType: string,
    spaceDevice: SpaceDevice,
    space: SelectOptions
  ) => {
    const mappingFunction = attributeTypeFieldMap[attributeType];
    if (!mappingFunction) {
      console.warn(
        `No space device field mapping function found for attribute type: ${attributeType}`
      );
      return;
    }
    const { key, subKey, sortKey } = mappingFunction(spaceDevice);

    const spaceAttributeConfig = this.spaceAttributes[attributeType];
    const targetAttributes = this.getMatchingAttributes(
      spaceAttributeConfig.options,
      key
    );
    const subTargetAttributes = this.getMatchingAttributes(
      _.flatMap(targetAttributes, 'options'),
      subKey
    );

    if (_.isEmpty(targetAttributes)) return;

    const attributeTypeMap = _.get(this.deviceMap[space.value], attributeType, {
      title: spaceAttributeConfig.title,
      options: [] as SpaceAttribute[]
    });
    const unified = _.unionBy(
      attributeTypeMap.options,
      [...targetAttributes, ...subTargetAttributes],
      'value'
    );
    const mergedAttributes = sortKey ? _.sortBy(unified, sortKey) : unified;
    _.set(this.deviceMap, `${space.value}.${attributeType}`, {
      title: attributeTypeMap.title,
      options: mergedAttributes
    });
    this.updateSpaceAttributeMap(attributeType, targetAttributes, space);
    this.updateSpaceAttributeMap(attributeType, subTargetAttributes, space);
  };

  fetchSpaceAndUpdateMaps = async () => {
    this.event.fireEvent(this);
    for (const spaceDevice of this.spaceDevices) {
      // POS-7996：只用可投遞（正式=ON、測試=TESTING）的版位建屬性 map 與 hover
      // 清單，排除 BANNED 等已下架版位，讓清單與「門市數量」一致。
      if (!this.isDeliverableSpace(spaceDevice)) {
        continue;
      }
      const space =
        this.spaces.find((s) =>
          _.includes(s.value.toString(), spaceDevice.spaceId)
        ) || ({} as SelectOptions);
      for (const attributeType of _.keys(this.spaceAttributes)) {
        this.setUpAttributeMaps(attributeType, spaceDevice, space);
      }
    }
    await this.setUpSpaceDetailMap();
    this.limitationSettingResetHook &&
      this.limitationSettingResetHook(
        this.getLimitationInventorySetting(),
        true
      );
    // Create new references so React's useMemo detects the change
    this.spaceAttributeMap = { ...this.spaceAttributeMap };
    this.deviceMap = { ...this.deviceMap };
    // Mark this testMode as fetched by seeding the cache entry. Future toggles
    // detect "已經 fetch 過" by `testModeCache.has(testMode)` — see setTestMode.
    this.testModeCache.set(this.testMode, {
      spaces: this.spaces,
      spaceDevices: this.spaceDevices,
      spaceAttributeMap: this.spaceAttributeMap,
      deviceMap: this.deviceMap,
      spaceDetailMap: this.spaceDetailMap,
      spaceAttributeSettings: this.spaceAttributeSettings,
      spaceAttributeLabelMaps: this.spaceAttributeLabelMaps,
      extras: this.captureTestModeExtras()
    });
    this.event.fireEvent(this);
  };

  importLimitationsFromCsvData = (
    limitationModel: EditLimitationModel,
    dataLines: string[]
  ) => {
    const deliveryTargetLabelMap = new Map(
      this.spaces.map((s) => [s.label, s])
    );

    const limitationValue = limitationModel.limitationValue;
    if (!limitationValue['include']) {
      limitationValue['include'] = [];
    }
    const deliveryUnit = this.channelGroupSetting.config.deliveryUnit;
    const limitationType =
      deliveryUnit === DeliveryUnit.STORE ? 'storeNum' : 'deviceCharacteristic';
    let customDeliveryTargetLimitation = limitationValue['include'].find(
      (l) => l.type === limitationType
    );
    if (!customDeliveryTargetLimitation) {
      customDeliveryTargetLimitation = {
        op: 'inc',
        type: limitationType,
        value: []
      };
      limitationValue['include'].push(customDeliveryTargetLimitation);
    }

    const existingValues = new Set(
      Array.isArray(customDeliveryTargetLimitation.value)
        ? customDeliveryTargetLimitation.value.map((v) => v.value)
        : []
    );

    for (const line of dataLines) {
      const name = line.trim();
      if (!name) continue;
      const deliveryTarget = deliveryTargetLabelMap.get(name);
      const value = deliveryTarget ? deliveryTarget.value.toString() : name;
      const label = name;
      if (!existingValues.has(value)) {
        (customDeliveryTargetLimitation.value as any[]).push({ label, value });
        existingValues.add(value);
      }
    }

    limitationModel.updateLimitationValue(limitationValue);
  };

  private getMatchingAttributes(
    options: SpaceAttribute[],
    value: string | string[] | undefined
  ): SpaceAttribute[] {
    if (!value) return [];
    return options.filter(
      (option) => option && _.includes(value, option.value.toString())
    );
  }

  private updateSpaceAttributeMap(
    attributeType: string,
    values: SpaceAttribute[],
    space: SelectOptions
  ) {
    for (const spaceAttribute of values) {
      const devices = _.get(
        this.spaceAttributeMap[attributeType],
        spaceAttribute.value,
        []
      );
      _.setWith(
        this.spaceAttributeMap,
        `${attributeType}.${spaceAttribute.value}`,
        _.unionBy(devices, [space], 'value'),
        Object
      );
    }
  }

  protected stripSpacePrefix(value: string): string {
    if (value.startsWith('asiamax\\')) {
      return value.replace(/asiamax\\/, '');
    }
    return value.replace(/\\/, '');
  }

  // ECOPD-5255: cached spaceId → aggregated health, rebuilt when spaceDevices change.
  private healthIndexSource?: SpaceDevice[];
  private healthIndex: Map<string, AggregatedSpaceHealth | undefined> =
    new Map();
  // 固定門市（fixOutdoorSpace）：以「真實 spaceId」為 key 的健康索引。門市層的
  // spaceId 被 remap 成 municipality/storeNum 後多台會被聚合，這個索引保留每一台
  // 的原始 rawSpaceId，讓被固定的那一台能被精準對出，不走 ON 優先聚合。
  private healthBySpaceId: Map<string, AggregatedSpaceHealth | undefined> =
    new Map();

  private rebuildHealthIndexes(): void {
    if (this.healthIndexSource === this.spaceDevices) return;
    this.healthIndex = new Map();
    this.healthBySpaceId = new Map();
    const grouped = _.groupBy(this.spaceDevices, 'spaceId');
    for (const [spaceId, devices] of Object.entries(grouped)) {
      this.healthIndex.set(spaceId, aggregateHealthState(devices));
    }
    // 以 rawSpaceId（remap 前真實 spaceId）建索引；key 做 stripSpacePrefix 正規化，
    // 與 getChipHealth 查詢端對齊，避免一邊帶 `asiamax\` 前綴而 miss。
    const groupedByRaw = _.groupBy(
      this.spaceDevices.filter((device) => device.rawSpaceId),
      (device) => this.stripSpacePrefix(String(device.rawSpaceId))
    );
    for (const [rawSpaceId, devices] of Object.entries(groupedByRaw)) {
      this.healthBySpaceId.set(rawSpaceId, aggregateHealthState(devices));
    }
    this.healthIndexSource = this.spaceDevices;
  }

  /**
   * ECOPD-5255: resolve the aggregated operational status for a chip.
   * `spaceDevices` (loaded in init) is remapped so each device's `spaceId`
   * equals the primary chip value (storeNum / deviceCharacteristic attribute),
   * so we match directly — no dependency on the lazily-populated
   * spaceAttributeMap (only built once the map overlay opens). Returns undefined
   * when the chip has no abnormal space, keeping its default appearance.
   *
   * 固定門市（fixOutdoorSpace）開啟且此 chip 帶有 spaceId 時，改用該 spaceId 直接
   * 對出「被固定的那一台」狀態（healthBySpaceId），而不是把同門市多台聚合成一個
   * ON 優先結果——這樣壞掉的那台就會如實顯示壞掉。
   */
  protected getChipHealth(
    option: SelectOptions
  ): AggregatedSpaceHealth | undefined {
    this.rebuildHealthIndexes();
    if (this.isFixOutdoorSpace && option.spaceId) {
      const health = this.healthBySpaceId.get(
        this.stripSpacePrefix(String(option.spaceId))
      );
      if (health) return health;
    }
    return this.healthIndex.get(String(option.value));
  }

  override get onlyBidCap(): boolean {
    return true;
  }

  get mapDeviceType(): string {
    return 'customOutdoorDevice';
  }

  override get limitationConfig(): LimitationConfig {
    return {
      defaultLimitationToShow: {
        name: 'customOutdoorDevice',
        operate: OPERATE.INCLUDE
      },
      supportAgeGenderLimitation: false,
      supportAudienceEstimation: false,
      requireIncludeLimitation: false,
      operationSet: {
        need: [OPERATE.INCLUDE],
        notNeed: [OPERATE.EXCLUDE],
        other: []
      }
    };
  }

  abstract get campaignAdType(): string;

  abstract getRtbOptionsMap(): {
    [objective: string]: Dictionary<L2ObjectOptimizationGoal[] | undefined>;
  };

  abstract getLimitationInventorySetting(): LimitationInventorySettings[];

  override getThirdPartyTrackingTypeOptions() {
    return [
      {
        label: i18n.t(
          'campaign.strategyAndTracking.thirdPartyTrackingTypePixel'
        ),
        value: ViewTrackingType.PIXEL
      }
    ];
  }

  override getThirdPartyTriggerTypeOptions() {
    return [
      {
        label: i18n.t('campaign.strategyAndTracking.impressionTrigger'),
        value: ViewTrackingTrigger.IMPRESSION
      }
    ];
  }
}

export abstract class AbstractOutdoorDeviceFormModel extends AbstractOutdoorLimitationCampaignFormModel {
  spaceIds: string[] = [];
  spaceDeviceMapping: { [spaceId: string]: SpaceDevice } = {};

  override get mapDeviceType(): string {
    return 'deviceCharacteristic';
  }

  override get notSupportSpaceAttributes(): string[] {
    return ['storeNum', 'nearbyPlaceLabel'];
  }

  protected override get spaceExtraGetter() {
    return deviceCharacteristicInfoExtraGetter;
  }

  protected override get spaceCountI18nKey(): string {
    return 'limitation.hints.deviceCount';
  }

  override async setUpSpaceDetailMap() {
    if (!_.isEmpty(this.spaceDetailMap)) return;
    const spaceDevicesGroupByDeviceCharacteristic = _.groupBy(
      this.spaceDevices,
      'spaceId'
    );
    this.spaceDetailMap = Object.values(
      spaceDevicesGroupByDeviceCharacteristic
    ).reduce((map: Record<string, SpaceDevice>, devices: SpaceDevice[]) => {
      const device = devices[0];
      if (device) {
        map[device.spaceId] = device;
      }
      return map;
    }, {});
  }

  override getSpaces = async (): Promise<SelectOptions[]> => {
    const spaces = await adRequestSourceManager.getSpacesWithConditions(
      false,
      {},
      this.adFormat,
      this.outdoorChannelId,
      deviceCharacteristicInfoExtraGetter,
      this.testMode
    );
    const spaceGroupByDeviceCharacteristic = _.groupBy(spaces, 'extra');

    this.spaceIds = _.uniq([
      ...this.spaceIds,
      ...spaces.map((space) => space.value.toString())
    ]);
    const deviceCharacteristicLabelMap = this.spaceAttributeLabelMaps.get(
      'deviceCharacteristic'
    );
    return Object.values(spaceGroupByDeviceCharacteristic).map(
      (spacesOfDevice) => {
        const sortedSpaces = spacesOfDevice.sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        const value = _.get(sortedSpaces[0], 'extra', '');
        return {
          label: deviceCharacteristicLabelMap?.get(value) ?? value,
          value
        };
      }
    );
  };

  override async getSpaceDevices(): Promise<SpaceDevice[]> {
    const spaceDevices = await rtbCmapignManager.getOutdoorSpacesDetail(
      this.spaceIds,
      this.testMode
    );
    const deviceCharacteristicLabelMap = this.spaceAttributeLabelMaps.get(
      'deviceCharacteristic'
    );
    return spaceDevices.flatMap((spaceDevice) => {
      const deviceValue = this.spaceExtraGetter(spaceDevice);
      // 組不出投遞鍵就整筆不收：選項樹在 spaces-with-attributes 那層也是同樣處置，
      // 留著只會生出一個選不到、也對不回任何 chip 的假選項值。
      if (!deviceValue) {
        return [];
      }
      const deviceCharacteristicSpace = {
        ...spaceDevice,
        // 保留真實 spaceId（見 store 模式說明）：固定機台時用來對出被固定的那一台。
        rawSpaceId: spaceDevice.spaceId,
        spaceId: deviceValue,
        spaceName: deviceCharacteristicLabelMap?.get(deviceValue) ?? deviceValue
      };
      this.spaceDeviceMapping[spaceDevice.spaceId] = deviceCharacteristicSpace;
      return [deviceCharacteristicSpace];
    });
  }

  override async getSpaceGroups(): Promise<SelectOptions[]> {
    const spaceGroups: SelectOptions[] =
      await adRequestSourceManager.getSpacesGroupsOfAdType(this.campaignAdType);
    return spaceGroups.map((spaceGroup) => {
      const extra = _.defaultTo(spaceGroup.extra, []);
      return {
        ...spaceGroup,
        extra: extra.map((space: SelectOptions) => {
          const deviceCharacteristicSpace =
            this.spaceDeviceMapping[
              this.stripSpacePrefix(space.value.toString())
            ];
          return deviceCharacteristicSpace
            ? {
                label: deviceCharacteristicSpace.spaceName,
                value: deviceCharacteristicSpace.spaceId
              }
            : space;
        })
      };
    });
  }

  override getLimitationInventorySetting(): LimitationInventorySettings[] {
    return [
      ...this.spaceAttributeSettings,
      this.systemRecommendOutdoorDeviceInventorySetting,
      {
        name: 'adx',
        hideLimitation: true,
        ignoreValidateOption: true,
        ignoreAddonFeature: true
      }
    ];
  }
}

export abstract class AbstractOutdoorStoreFormModel extends AbstractOutdoorLimitationCampaignFormModel {
  spaceIds: string[] = [];
  spaceStoreMapping: { [spaceId: string]: SpaceDevice } = {};

  override get mapDeviceType(): string {
    return 'storeNum';
  }

  override get notSupportSpaceAttributes(): string[] {
    return ['nearbyPlaceLabel', 'deviceCharacteristic'];
  }

  protected override get spaceExtraGetter() {
    return storeInfoExtraGetter;
  }

  protected override get spaceCountI18nKey(): string {
    return 'limitation.hints.storeCount';
  }

  // POS-8002：門市顯示名稱一律組成「縣市_店號_店名」，與後端 store-options 回傳的
  // label 格式對齊，讓 Banned 門市（不在 store-options）也能組出相同格式的名稱。
  protected buildStoreLabel(
    device: Pick<SpaceDevice, 'municipality' | 'storeNum' | 'locationName'>
  ): string {
    return `${device.municipality}_${device.storeNum}_${device.locationName}`;
  }

  override async setUpSpaceDetailMap() {
    if (!_.isEmpty(this.spaceDetailMap)) return;
    const spaceDevicesGroupByStoreInfo = _.groupBy(
      this.spaceDevices,
      this.mapDeviceType
    );
    this.spaceDetailMap = Object.values(spaceDevicesGroupByStoreInfo).reduce(
      (map: Record<string, SpaceDevice>, devices: SpaceDevice[]) => {
        const device = devices[0];
        if (device) {
          map[device.spaceId] = device;
        }
        return map;
      },
      {}
    );
  }

  override getSpaces = async (): Promise<SelectOptions[]> => {
    const spaces = await adRequestSourceManager.getSpacesWithConditions(
      false,
      {},
      this.adFormat,
      this.outdoorChannelId,
      storeInfoExtraGetter,
      this.testMode
    );
    const spaceGroupByStoreInfo = _.groupBy(spaces, 'extra');

    this.spaceIds = _.uniq([
      ...this.spaceIds,
      ...spaces.map((space) => space.value.toString())
    ]);
    const storeNumLabelMap = this.spaceAttributeLabelMaps.get('storeNum');
    return Object.values(spaceGroupByStoreInfo).map((spacesOfStore) => {
      const sortedSpaces = spacesOfStore.sort((a, b) =>
        a.label.localeCompare(b.label)
      );
      const value = _.get(sortedSpaces[0], 'extra', '');
      return {
        label: storeNumLabelMap?.get(value) ?? value,
        value
      };
    });
  };

  override async getSpaceDevices(): Promise<SpaceDevice[]> {
    const spaceDevices = await rtbCmapignManager.getOutdoorSpacesDetail(
      this.spaceIds,
      this.testMode
    );
    const storeNumLabelMap = this.spaceAttributeLabelMaps.get('storeNum');
    return spaceDevices.flatMap((spaceDevice) => {
      const storeValue = this.spaceExtraGetter(spaceDevice);
      // 組不出投遞鍵就整筆不收（同機台模式）。
      if (!storeValue) {
        return [];
      }
      // POS-8002：storeNumLabelMap 建自 store-options（只回 ON 版位），故被 Banned 的
      // 門市不在其中；chip label（載入時的 overlayLabels）與此處 spaceName 原本都會
      // fallback 成 storeValue（縣市/店號）而掉了店名。spaces-detail 明細本身即含 Banned
      // 版位並帶 municipality/storeNum/locationName，這裡把缺漏的門市補進 label map，
      // 組出與 store-options 一致的「縣市_店號_店名」，讓操作人員仍能辨識哪一間門市關閉
      // （Banned 顏色/icon 另由 health-state 呈現）。補在 getSpaceDevices（init 內、
      // limitation label overlay 之前）確保 chip 也吃得到。
      if (
        storeNumLabelMap &&
        !storeNumLabelMap.has(storeValue) &&
        spaceDevice.locationName
      ) {
        storeNumLabelMap.set(storeValue, this.buildStoreLabel(spaceDevice));
      }
      const storeDevice = {
        ...spaceDevice,
        // 保留真實 spaceId：spaceId 會被 remap 成 storeValue（同門市多台會聚合），
        // 固定門市要靠這個原始值對出「被固定的那一台」狀態。
        rawSpaceId: spaceDevice.spaceId,
        spaceId: storeValue,
        spaceName: storeNumLabelMap?.get(storeValue) ?? storeValue
      };
      this.spaceStoreMapping[spaceDevice.spaceId] = storeDevice;
      return [storeDevice];
    });
  }

  override async getSpaceGroups(): Promise<SelectOptions[]> {
    const spaceGroups: SelectOptions[] =
      await adRequestSourceManager.getSpacesGroupsOfAdType(this.campaignAdType);
    return spaceGroups.map((spaceGroup) => {
      const extra = _.defaultTo(spaceGroup.extra, []);
      return {
        ...spaceGroup,
        extra: extra.map((space: SelectOptions) => {
          const storeDevice =
            this.spaceStoreMapping[
              this.stripSpacePrefix(space.value.toString())
            ];
          return storeDevice
            ? {
                label: storeDevice.spaceName,
                value: storeDevice.spaceId
              }
            : space;
        })
      };
    });
  }

  override getLimitationInventorySetting(): LimitationInventorySettings[] {
    const originalStoreNumSetting = this.spaceAttributeSettings.find(
      (setting) => setting.name === 'storeNum'
    ) as LimitationInventorySettings;

    const settings = this.spaceAttributeSettings.map((setting) => {
      if (setting.name !== 'storeNum' || !originalStoreNumSetting) {
        return setting;
      }
      const storeNumSetting = { ...originalStoreNumSetting };
      storeNumSetting.component = SelectComponent;
      // POS-8020：storeNum 的 cb 以 store-options（只回 ON 版位）為底，使用者原本
      // 已選但後來被 BANNED 的門市會被 validateLimitationValue 判為無效並擋掉存檔。
      // picker 本來就只允許選有效門市，post-hoc 出現的 stale 值（BANNED / 刪除 /
      // 改名）容忍即可，不需要擋存檔；直接關閉此欄位的選項合法性檢查。
      storeNumSetting.ignoreValidateOption = true;
      return storeNumSetting;
    });

    return [
      ...settings,
      this.systemRecommendOutdoorDeviceInventorySetting,
      {
        name: 'adx',
        hideLimitation: true,
        ignoreValidateOption: true,
        ignoreAddonFeature: true
      }
    ];
  }
}

export abstract class AbstractOutdoorSynchronizedPlaybackFormModel extends AbstractOutdoorStoreFormModel {
  spaceIds: string[] = [];
  spaceStoreMapping: { [spaceId: string]: SpaceDevice } = {};
  channelGroupManager: ChannelGroupManager = new DefaultChannelGroupManager();
  deviceUnionFind: DeviceUnionFind = new DeviceUnionFind();

  override get mapDeviceType(): string {
    return 'simulcastGroup';
  }
  simulcastGroupSpaceIds: Map<string | number, string[]> = new Map();
  simulcastGroups: SelectOptions[] = [];

  override get limitationConfig(): LimitationConfig {
    return {
      supportAgeGenderLimitation: false,
      supportAudienceEstimation: false,
      requireIncludeLimitation: false,
      operationSet: {
        need: [OPERATE.INCLUDE],
        // POS-8073：「其他條件」區塊要留著 —— 測試模式開關住在這裡（EditLimitation
        // 以 operationSet.other 是否存在當整區的 guard）。ECOPD-4930 拿掉同播不需要的
        // 區塊時把它一併拿掉，於是經銷商端開了 testMode add-on 也顯示不出開關。
        // 「排除投遞流量方向」(notNeed) 維持不給，那是 ECOPD-4930 的刻意決定。
        other: []
      }
    };
  }

  /**
   * POS-8073：同播的群組清單與 spaceId 對照都是「隨 testMode 而異」的資料
   * （init / getSpaces 都帶 this.testMode 去查），必須跟著 testModeCache 一起
   * 快照/還原。否則來回切換測試模式命中 cache 時，群組下拉的 cb 會讀到另一個
   * 模式的 this.simulcastGroups。同 POS-8002 的 storeNum label map 那類問題。
   */
  protected override captureTestModeExtras() {
    return {
      simulcastGroups: this.simulcastGroups,
      simulcastGroupSpaceIds: this.simulcastGroupSpaceIds,
      spaceIds: this.spaceIds,
      // deviceUnionFind 由 init 的 buildSimulcastGroupMappings 依當次群組重建，
      // 命中 cache 的路徑不會重跑 init，所以也要進快照。
      deviceUnionFind: this.deviceUnionFind
    };
  }

  protected override restoreTestModeExtras(extras: unknown) {
    if (!extras) return;
    const {
      simulcastGroups,
      simulcastGroupSpaceIds,
      spaceIds,
      deviceUnionFind
    } = extras as ReturnType<
      AbstractOutdoorSynchronizedPlaybackFormModel['captureTestModeExtras']
    >;
    this.simulcastGroups = simulcastGroups;
    this.simulcastGroupSpaceIds = simulcastGroupSpaceIds;
    this.spaceIds = spaceIds;
    this.deviceUnionFind = deviceUnionFind;
  }

  override async init() {
    try {
      this.simulcastGroups = await this.channelGroupManager.getSimulcastGroups(
        this.channelGroupSetting.config.id,
        this.testMode
      );
      this.spaces = await this.getSpaces();
      this.spaceDevices = await this.getSpaceDevices();
      this.buildSimulcastGroupMappings(this.simulcastGroups);

      const attributeName = this.mapDeviceType;
      this.spaceAttributeSettings = [
        {
          name: DEFAULT_INVENTORY
        },
        {
          name: attributeName,
          title: i18n.t(`limitation.labels.simulcastGroup`),
          singleSelect: false,
          itemSetting: defaultItemSetting(),
          searchPlaceholder: 'productGroup.placeholders.search',
          ignoreAddonFeature: true,
          supportOperates: [OPERATE.INCLUDE],
          sidebarTitleKey: this.sidebarTitleKey,
          tipsConfig: {
            debounceTrigger: 500,
            renderTips: (playbackGroup) => {
              return renderSpacesOfSynchronizedPlaybackGroup(
                playbackGroup.value.toString(),
                this.channelGroupSetting.config.id,
                // POS-8073：tooltip 也要跟著模式，否則測試模式下列出來的是空的。
                this.testMode
              );
            }
          },
          component: SelectComponent,
          cb: async () =>
            this.simulcastGroups.map((group) => ({
              ...group,
              extra: this.spaceAttributeMap.simulcastGroup?.[group.value] || []
            }))
        }
      ];
    } catch (e) {
      this.spaceAttributeSettings = [
        {
          name: DEFAULT_INVENTORY
        }
      ];
      console.error(e);
    }
  }

  override getSpaces = async (): Promise<SelectOptions[]> => {
    // POS-8073：spaceIds 與群組→版位對照都由當次 testMode 的群組重新組出來，
    // 不沿用上一輪的殘留（切換測試模式時本來會把另一個模式的 spaceId 一路帶進
    // 後續查詢）。simulcastGroupSpaceIds 一定要換新 Map —— 只 .set() 不換實例的話，
    // 兩個模式的快照會指向同一個 Map，captureTestModeExtras 對它等於沒作用。
    this.spaceIds = [];
    this.simulcastGroupSpaceIds = new Map();
    for (const group of this.simulcastGroups) {
      const groupSpaceIds =
        await this.channelGroupManager.getSpaceIdsOfSimulcastGroup(
          group.value.toString(),
          this.testMode
        );
      this.simulcastGroupSpaceIds.set(group.value, groupSpaceIds);
      this.spaceIds = _.uniq([...this.spaceIds, ...groupSpaceIds]);
    }
    const spaces = await adRequestSourceManager.getSpacesWithConditions(
      false,
      { spaceId: this.spaceIds },
      this.adFormat,
      this.outdoorChannelId,
      undefined,
      this.testMode
    );
    return spaces.map((space) => ({
      label: space.label,
      value: space.value.toString()
    }));
  };

  override async getSpaceDevices(): Promise<SpaceDevice[]> {
    return rtbCmapignManager.getOutdoorSpacesDetail(
      this.spaceIds,
      this.testMode
    );
  }

  override async setUpSpaceDetailMap() {
    if (!_.isEmpty(this.spaceDetailMap)) return;
    this.spaceDetailMap = _.keyBy(this.spaceDevices, 'spaceId');
  }

  override fetchSpaceAndUpdateMaps = async () => {
    this.event.fireEvent(this);
    await this.setUpSpaceDetailMap();
    this.limitationSettingResetHook &&
      this.limitationSettingResetHook(
        this.getLimitationInventorySetting(),
        true
      );
    // Create new references so React's useMemo detects the change
    this.spaceAttributeMap = { ...this.spaceAttributeMap };
    this.deviceMap = { ...this.deviceMap };
    // Seed testModeCache so setTestMode can tell this mode is already fetched.
    this.testModeCache.set(this.testMode, {
      spaces: this.spaces,
      spaceDevices: this.spaceDevices,
      spaceAttributeMap: this.spaceAttributeMap,
      deviceMap: this.deviceMap,
      spaceDetailMap: this.spaceDetailMap,
      spaceAttributeSettings: this.spaceAttributeSettings,
      spaceAttributeLabelMaps: this.spaceAttributeLabelMaps,
      extras: this.captureTestModeExtras()
    });
    this.event.fireEvent(this);
  };

  private buildSimulcastGroupMappings(groups: SelectOptions[]) {
    this.deviceUnionFind = new DeviceUnionFind();
    for (const space of this.spaces) {
      this.deviceUnionFind.makeSet(space.value);
    }
    const attributeName = this.mapDeviceType;
    // Map raw spaceId → space value (which may have 'asiamax\' prefix)
    const rawIdToSpaceValue = new Map<string, string>();
    for (const space of this.spaces) {
      const rawId = this.stripSpacePrefix(space.value.toString());
      rawIdToSpaceValue.set(rawId, space.value.toString());
    }
    for (const group of groups) {
      const spaceValues = (this.simulcastGroupSpaceIds.get(group.value) || [])
        .map((id) => rawIdToSpaceValue.get(id))
        .filter(Boolean) as string[];

      // Union all devices to the group value as root
      this.deviceUnionFind.makeSet(group.value, group);
      for (const spaceValue of spaceValues) {
        this.deviceUnionFind.union(group.value, spaceValue);
      }

      // spaceAttributeMap: simulcastGroup → groupValue → [devices]
      const devices: Device[] = spaceValues.map((spaceValue) => {
        const space = this.spaces.find(
          (s) => s.value.toString() === spaceValue
        );
        return space || { label: spaceValue, value: spaceValue };
      });
      _.setWith(
        this.spaceAttributeMap,
        `${attributeName}.${group.value}`,
        devices,
        Object
      );

      // deviceMap: spaceValue → { simulcastGroup: { title, options: [group] } }
      for (const spaceValue of spaceValues) {
        const deviceAttrs = this.deviceMap[spaceValue] || {};
        const existing = deviceAttrs[attributeName] || {
          title: i18n.t('limitation.labels.simulcastGroup'),
          options: [] as SpaceAttribute[]
        };
        _.set(this.deviceMap, `${spaceValue}.${attributeName}`, {
          title: existing.title,
          options: _.unionBy(existing.options, [group], 'value')
        });
      }
    }
  }

  override getLimitationInventorySetting(): LimitationInventorySettings[] {
    return [
      ...this.spaceAttributeSettings,
      {
        name: 'adx',
        hideLimitation: true,
        ignoreValidateOption: true,
        ignoreAddonFeature: true
      }
    ];
  }
}
